import { canRetry, type StartFailure } from './parseStartError'
import { formatCredits } from './money'
import { imageFailureText } from './imageFailureText'
import { blockEditFailureText } from './blockEditFailureText'
import { translationFailureText } from './translationFailureText'
import { resumeFailureText } from './resumeFailureText'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

export interface FailureView {
  title: string
  /**
   * Действие рисуется ТОЛЬКО когда оно осмысленно: «повторить» — там, где
   * сервер объявил ретраебельность, «показать» — когда курс занят другим
   * прогоном, «стиль» — когда запуск отрисовки упёрся в пустой `imageStyle`,
   * «пополнить» — когда не хватает кредитов (под-проект G завёл диалог
   * пополнения, и вести стало куда).
   */
  action?: 'retry' | 'show-run' | 'style' | 'topup'
}

/**
 * Бизнес-коды отрисовки (§5.3), правки блоков (§6.1) и возобновления (§8) —
 * свой копирайт: серверный текст там технический («at most 200 images per
 * run, got 237») либо вовсе английский (`nothing_to_resume` и соседи —
 * ручка `POST .../resume` отдаёт их тем же `422`, каким отвечает и `hint`
 * при поллинге, финальное ревью п.2). Словари не пересекаются, картиночный
 * спрашивается первым. Остальные бизнес-коды бэк присылает человеческим
 * текстом, его и показываем. Вынесено из `switch` ради лимита сложности —
 * ветвление внутри `business` не трогает исчерпывающий `switch` по `kind`.
 */
const businessView = (code: string, message: string): FailureView => {
  const image = imageFailureText(code, message)
  if (image) {
    return code === 'image_style_required' ? { title: image, action: 'style' } : { title: image }
  }
  const blockEdit = blockEditFailureText(code, message)
  if (blockEdit) return { title: blockEdit }
  const translation = translationFailureText(code, message)
  if (translation) return { title: translation }
  const resume = resumeFailureText(code, message)
  if (resume) return { title: resume }
  return { title: message }
}

export interface FailureContext {
  /** См. `useReserveReturning`: в этом окне платить нечем. */
  reserveReturning?: boolean
}

/**
 * Нехватка кредитов читается по-разному в зависимости от того, не вернулся ли
 * ещё резерв прошлого прогона. Вынесено из `switch` ради лимита сложности —
 * исчерпывающий `switch` по `kind` этим не затронут.
 */
const noCreditsView = (
  failure: Extract<StartFailure, { kind: 'no_credits' }>,
  ctx?: FailureContext,
): FailureView => {
  if (ctx?.reserveReturning)
    // ВТОРОЙ сознательный случай кнопки «повторить» помимо `canRetry` (там
    // ретраебельность объявляет СЕРВЕР, здесь её объявляем мы). Повтор
    // осмыслен потому, что резерв прошлого прогона вернётся сам: это не
    // поломка и не настоящая нехватка — суммы отказа тут врут (баланс занижен
    // на ещё не разблокированный резерв), а «Пополнить» продавало бы кредиты,
    // которые у автора уже есть. Ветка выглядит ошибкой, только если не знать
    // про окно возврата, — не «чинить».
    return { title: messagesNow(messages).reserveReturning, action: 'retry' }
  const m = messagesNow(messages)
  const title =
    failure.required && failure.balance
      ? m.noCreditsSums(formatCredits(failure.required), formatCredits(failure.balance))
      : m.noCredits
  return { title, action: 'topup' }
}

export const failureText = (failure: StartFailure, ctx?: FailureContext): FailureView => {
  const m = messagesNow(messages)
  switch (failure.kind) {
    case 'not_configured':
      // Сегодняшний ответ на любой старт: графы n8n не запущены. Свой текст,
      // а не failure.message (тот — фолбэк parseStartError для мест, куда
      // failureText ещё не подключён). Нейтральная формулировка (ревью
      // Task 3, minor 2) — этот отказ бывает не только у генерации курса, но
      // и у правки блока, «Генерация...» там звучала бы неправдой.
      return { title: m.notConfiguredTitle }
    case 'unavailable':
      return {
        title: m.unavailable,
        action: canRetry(failure) ? 'retry' : undefined,
      }
    case 'no_credits':
      return noCreditsView(failure, ctx)
    case 'course_busy':
      // M5 (финальное ревью): `status` различает СВОЙ прогон на паузе от
      // чужого/своего идущего — без второй ветки эта же смета в
      // `CourseBusyNotice`/`DrawImagesBody` звала бы «ответить», а баннер
      // отказа 409 рядом — «ждать», хотя оба про один и тот же прогон.
      return failure.status === 'awaiting_author'
        ? { title: m.awaitingAnswer(failure.jobId), action: 'show-run' }
        : { title: m.courseBusyRun(failure.jobId), action: 'show-run' }
    case 'validation':
      // Нейтральная формулировка (ревью Task 3, minor 2): «число уроков»
      // относилось только к генерации курса — у правки блока такого параметра
      // нет, а `validation` бывает и на её старте.
      return { title: m.validationTitle }
    case 'forbidden':
      return { title: m.forbidden }
    case 'not_found':
      return { title: m.notFound }
    case 'rate_limited':
      return { title: failure.message }
    case 'business':
      return businessView(failure.code, failure.message)
    case 'already_resumed':
      // Без action: второй кнопки не рисуем, действие тут — перецепить
      // поллинг на jobId продолжения, а это дело того, кто ведёт чат, не
      // таблицы текстов.
      return { title: m.alreadyResumed }
    case 'rejected':
    case 'unknown':
      // Контракт: message у этих кодов — фиксированная строка бэка, показываем как есть.
      return { title: failure.message }
    default: {
      // Недостижимо по типам: ветка ловит будущий kind, которого ещё нет в
      // StartFailure — сборка упадёт здесь (never), а не покажет пустой
      // текст в UI молча.
      const _unhandled: never = failure
      void _unhandled
      return { title: m.startFailed }
    }
  }
}
